import { all, call, fork, put, select, take } from 'redux-saga/effects';

import { message } from 'antd';
import { updateNoteToCard } from 'containers/MainLayout/actions';
import translations from 'translations';
import {
  addFavorite,
  getProducts,
  handleWriteContactUsInfo,
  removeFavorite,
  setProductNote
} from 'utils/apollo';
import utilMessages from 'utils/messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { selectBuyer } from '../../MainLayout/selectors';
import {
  productSourceFavorite,
  productSourceSetNote,
  productsSource,
} from './actions';
import ActionTypes from './constants';
import { ISectionData, KamereoFreshSectionEnum } from './types';

export const CONTACT_ID_FROM_LANDING_PAGE = 'contactIdFromLandingPage';

const sectionInitData = () => {
  const lang = window.localStorage.getItem('lang') || 'vi';
  return {
    [KamereoFreshSectionEnum.Fruit]: {
      id: KamereoFreshSectionEnum.Fruit,
      title: lang === 'en' ? 'Pre-Cut Fruits' : 'Trái cây cắt sẵn',
      categoryIds: [17],
      banner: KamereoFreshSectionEnum.Fruit,
      data: [],
      error: false,
      fetching: true,
      totalResults: 0,
    },
    [KamereoFreshSectionEnum.ReadyToEat]: {
      id: KamereoFreshSectionEnum.ReadyToEat,
      title: lang === 'en' ? 'Ready-to-Eat Foods' : 'Thực phẩm ăn liền',
      categoryIds: [1, 16],
      banner: KamereoFreshSectionEnum.ReadyToEat,
      data: [],
      error: false,
      fetching: true,
      totalResults: 0,
    },
    [KamereoFreshSectionEnum.FreshIngredients]: {
      id: KamereoFreshSectionEnum.FreshIngredients,
      title: lang === 'en' ? 'Fresh Ingredients' : 'Nguyên liệu tươi sống',
      categoryIds: [3, 69],
      banner: KamereoFreshSectionEnum.FreshIngredients,
      data: [],
      error: false,
      fetching: true,
      totalResults: 0,
    },
  };
};

function* getSectionData(section: ISectionData) {
  const buyer = yield select(selectBuyer);
  const region = window.localStorage.getItem('region') || 'HCM';
  const response = yield call(getProducts, {
    sort: [{ field: 'name', order: 'ASC' }],
    filter: {
      tags: ['THUONG_HIEU_KAMEREO'],
      categoryIds: section.categoryIds,
      regionCode: buyer?.id ? buyer?.region : region || 'HCM',
    },
    isBoostKamereoProducts: true,
    pagination: { page: 0, size: 10 },
  });
  if (response.errors) {
    yield put(
      productsSource.failure({
        [section.id]: { data: [], error: false, fetching: false, totalResults: 0 },
      }),
    );
  } else {
    yield put(
      productsSource.success({
        [section.id]: {
          data: response.data,
          error: false,
          fetching: false,
          totalResults: response.totalResults,
        },
      }),
    );
  }
}
function* loadAllSectionData() {
  const sections = sectionInitData();
  yield put(productsSource.request(sections));
  yield all(
    Object.values(sections).map((section) => {
      return call(getSectionData, section);
    }),
  );
}

function* productSourceFavoriteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.PRODUCTS_SOURCE_FAVORITE);
    yield put(productSourceFavorite.request());
    const {
      product: { isFavorite, id },
      index,
      section,
    } = payload;

    const response = yield call(isFavorite ? removeFavorite : addFavorite, { id: id });
    if (response.errors) {
      yield put(productSourceFavorite.failure({ index: index, section: section }));
    } else {
      yield put(productSourceFavorite.success({ index: index, section: section, isFavorite: !isFavorite }));
    }
  }
}

function* productSourceSetNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_PRODUCT_NOTE);
    yield put(productSourceSetNote.request());

    const { id, note, index, section } = payload;
    const response = yield call(setProductNote, { input: { productId: id, note: note } });
    if (response.errors) {
      yield put(productSourceSetNote.failure(response.errors));
      message.error(translations(utilMessages[payload.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    } else {
      yield put(productSourceSetNote.success({ note: note, index: index, section: section }));
      yield put(updateNoteToCard(response.product));
      message.success(translations(utilMessages[payload.note ? 'addItemNoteSuccess' : 'deleteItemNoteSuccess']));
    }
  }
}


function* submitContactShortForm() {
  while (true) {
    const { payload } = yield take(ActionTypes.CONTACT_US_SHORT_FORM_REQUEST);
    const { data, cb } = payload;
    const response = yield call(handleWriteContactUsInfo, {
      input: {
        contactUsInfos: [data],
      },
    });
    if (!response.errors && cb) {
      cb();
      window.localStorage.setItem(CONTACT_ID_FROM_LANDING_PAGE, response?.contactUsInfoList?.[0]?.contactId || '');
      sendGA4Event(EventName.GET_PRODUCT_CONSULTATION_FORM_SUBMIT_FTB, {
        get_product_consultation_form_submit_status: 'SUCCESS',
      });
    } else {
      message.error('Something went wrong');
      sendGA4Event(EventName.GET_PRODUCT_CONSULTATION_FORM_SUBMIT_FTB, {
        get_product_consultation_form_submit_status: 'ERROR',
      });
    }
  }
}

export default function* saga() {
  yield fork(loadAllSectionData);
  yield fork(productSourceFavoriteFlow);
  yield fork(productSourceSetNoteFlow);
  yield fork(submitContactShortForm);
}
